import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faTimes,
  faWaveSquare,
  faUsers,
  faCapsules,
  faLink,
  faHeart,
  faSave,
  faChevronRight,
  faSearch,
  faChevronDown,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { fetchSystemCaregivers, fetchPracticeProviders, fetchPracticeCaregivers, fetchPractice } from '../../../../store/slices/practiceSlice';
import { createPatient, updatePatient } from '../../../../store/slices/patientSlice';
import patientService from '../../../../services/patientService';
import BpEnrollmentSettingsModal, { monitoringVitalKind } from './BpEnrollmentSettingsModal';
import { applyEnrollmentScheduleChoice } from '../../../../utils/enrollmentScheduleTargets';
import { readVitalScheduleTargetMode, writeVitalScheduleTargetMode } from '../../../../utils/patientScheduleTargetPreference';
import inventoryService from '../../../../services/inventoryService';
import careProgramService from '../../../../services/careProgramService';
import { normalizePatientStatusId, patientStatusFormValue } from '../../../../utils/patientStatusUtils';
import PatientStatusDeviceRemovalModal from '../../../../components/Common/PatientStatusDeviceRemovalModal/PatientStatusDeviceRemovalModal';
import {
  buildProgramTabsFromEnrolled,
  getProgramsForPractice,
  filterActiveCarePrograms
} from '../../../../utils/careProgramUtils';
import { TIME_ZONES, US_STATES, getStateIdFromName, EMERGENCY_CONTACT_RELATIONS } from '../../../../utils/constants';
import { formatMeterProductOptionLabel } from '../../../../utils/meterMappingUtils';
import { sanitizeEmailInput, getEmailValidationError } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import { applyPasswordServerFieldError } from '../../../../utils/passwordValidation';
import { AVAILABILITY_ERROR_MESSAGES, clearFieldError } from '../../../../utils/formValidation';
import { validateImageUploadFile } from '../../../../utils/imageUploadUtils';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import AppDateInput from '../../../../components/Common/AppDateInput/AppDateInput';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import PatientChronicConditionsSection from '../../../../components/PatientChronicConditions/PatientChronicConditionsSection';
import { serializePatientChronicConditions } from '../../../../utils/patientChronicConditionUtils';
import {
  PatientAscStyleSinglePicker,
  PatientAscStyleMultiPicker,
  buildCaregiverOptionsWithStubs,
  toNumericCaregiverIds,
  isStaffActive
} from '../shared/PatientCaregiverPickers';
import { isEvitalsMonitoringPractice } from '../../../../utils/practiceMonitoringUtils';
import lookupService from '../../../../services/lookupService';
import IcdCodeSearchField from '../../../../components/Common/IcdCodeSearchField/IcdCodeSearchField';
import {
  buildMonitoringTypesPayload,
  emptyMonitoringIcdValues,
  getIcdOptionsForMonitoringLabel,
  groupIcdCodesByMeasureType,
  parsePatientMonitoringSelection,
} from '../../../../utils/patientMonitoringIcdUtils';
import './AddPatient.css';
import './AddPatientModal.css';

function emptyAddPatientFormState() {
  return {
    title: '', firstName: '', middleInitial: '', lastName: '', addressLine1: '', addressLine2: '', city: '', state: '', zipCode: '',
    homePhone: '', cellPhone: '', fax: '', emergencyContactFirstName: '', emergencyContactLastName: '', emergencyContactRelation: '', emergencyContactPhone: '', emergencyContactAddress: '', email: '', gender: 'Male', dateOfBirth: '', twoWayAuth: 'Enabled', mobileAppAccess: 'Disabled', status: 'Active',
    timeZone: '', rpmStartDate: '', provider: '', username: '', password: '', confirmPassword: '', ssn: '',
    systemCaregiverIds: [], practiceCaregiverIds: []
  };
}

const AddPatient = () => {
  const navigate = useNavigate();
  const { practiceId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get('editId');
  const isEditMode = !!editId;
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const { systemCaregivers = [], practiceProviders = [], practiceCaregivers = [], currentPractice } = useSelector(state => state.practice ?? {});
  const safeSystemCaregivers = Array.isArray(systemCaregivers) ? systemCaregivers : (systemCaregivers?.caregivers || systemCaregivers?.data || []);
  const safePracticeProviders = Array.isArray(practiceProviders) ? practiceProviders : (practiceProviders?.data || []);
  const safePracticeCaregivers = Array.isArray(practiceCaregivers) ? practiceCaregivers : (practiceCaregivers?.data || []);
  const requiresSystemCaregiver = isEvitalsMonitoringPractice(currentPractice);

  const isActive = (item) => isStaffActive(item);
  const activeSystemCaregivers = useMemo(() => safeSystemCaregivers.filter(isActive), [safeSystemCaregivers]);
  const activePracticeProviders = useMemo(() => safePracticeProviders.filter(isActive), [safePracticeProviders]);
  const activePracticeCaregivers = useMemo(() => safePracticeCaregivers.filter(isActive), [safePracticeCaregivers]);

  const [formData, setFormData] = useState(() => emptyAddPatientFormState());
  const [selectedCarePrograms, setSelectedCarePrograms] = useState([]);
  const [selectedMonitoring, setSelectedMonitoring] = useState([]);
  const vitalConfirmedRef = useRef({ bp: false, bg: false, weight: false });
  const [vitalModal, setVitalModal] = useState(null);
  const [vitalModes, setVitalModes] = useState({ bp: null, bg: null, weight: null });
  const [vitalDrafts, setVitalDrafts] = useState({ bp: null, bg: null, weight: null });
  const [monitoringIcdValues, setMonitoringIcdValues] = useState(() => emptyMonitoringIcdValues());
  const [icdCodesByMeasureType, setIcdCodesByMeasureType] = useState({ 1: [], 2: [], 3: [] });
  const [selectedChronicConditions, setSelectedChronicConditions] = useState([]);
  const [ccmRestrictionsConfirmed, setCcmRestrictionsConfirmed] = useState(false);
  const [products, setProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [carePrograms, setCarePrograms] = useState([]);
  const [errors, setErrors] = useState({});
  const [initialPatient, setInitialPatient] = useState(null);
  const [mappedDevices, setMappedDevices] = useState([]);
  const [statusDeviceRemovalModal, setStatusDeviceRemovalModal] = useState({ show: false, targetStatus: '' });

  useEffect(() => {
    if (isEditMode && editId && practiceId) {
      patientService.getPatientFastDetails(practiceId, editId)
        .then((res) => {
          if (res?.data?.success && res.data.data) {
            const p = res.data.data;
            setInitialPatient(p);
            setFormData((prev) => {
              const record = p.patient || p;
              return {
                ...prev,
                status: patientStatusFormValue(p.status),
                emergencyContactFirstName: record.emergency_contact_first_name || record.emergencyContactFirstName || '',
                emergencyContactLastName: record.emergency_contact_last_name || record.emergencyContactLastName || '',
                emergencyContactRelation: record.emergency_contact_relation || record.emergencyContactRelation || '',
                emergencyContactPhone: formatUSPhoneNumber(record.emergency_contact_phone || record.emergencyContactPhone || ''),
                emergencyContactAddress: record.emergency_contact_address || record.emergencyContactAddress || ''
              };
            });
            const { labels: loadedMonitoring, icdValues: loadedMonitoringIcd } = parsePatientMonitoringSelection(p);
            if (loadedMonitoring.length) {
              setSelectedMonitoring(loadedMonitoring);
              setMonitoringIcdValues(loadedMonitoringIcd);
            }
            const nextModes = { bp: null, bg: null, weight: null };
            [
              ['Blood Pressure', 'bp'],
              ['Blood Glucose', 'bg'],
              ['Weight', 'weight']
            ].forEach(([type, kind]) => {
              if (!loadedMonitoring.includes(type)) return;
              const storedMode = readVitalScheduleTargetMode(practiceId, editId, kind);
              nextModes[kind] = storedMode === 'custom' ? 'custom' : 'practice';
            });
            setVitalModes(nextModes);
            setVitalDrafts({ bp: null, bg: null, weight: null });
            vitalConfirmedRef.current = { bp: false, bg: false, weight: false };
          }
        })
        .catch(() => {});
      patientService.getPatientMeters(practiceId, editId)
        .then((res) => {
          if (res?.data?.success && Array.isArray(res.data.data)) {
            setMappedDevices(res.data.data);
          }
        })
        .catch(() => {});
    }
  }, [isEditMode, editId, practiceId]);

  const handleConfirmStatusDeviceRemoval = () => {
    const target = statusDeviceRemovalModal.targetStatus || 'Pending';
    setFormData((prev) => ({ ...prev, status: target }));
    setMappedDevices([]);
    setStatusDeviceRemovalModal({ show: false, targetStatus: '' });
    toast.info(`Assigned devices removed. Status set to ${target}. Click Save to apply.`);
  };

  const handleCancelStatusDeviceRemoval = () => {
    setStatusDeviceRemovalModal({ show: false, targetStatus: '' });
    setFormData((prev) => ({ ...prev, status: 'Active' }));
  };

  const pictureInputRef = useRef(null);
  const [pictureFile, setPictureFile] = useState(null);

  const handlePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const pictureError = validateImageUploadFile(file, { label: 'Profile picture' });
    if (pictureError) {
      setErrors((prev) => ({ ...prev, profilePicture: pictureError }));
      e.target.value = '';
      return;
    }
    clearFieldError(setErrors, 'profilePicture');
    setPictureFile(file);
  };

  const handleRemovePicture = () => {
    setPictureFile(null);
    clearFieldError(setErrors, 'profilePicture');
    if (pictureInputRef.current) pictureInputRef.current.value = '';
  };

  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    scheduleSsnCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    handleEmailBlur,
    ensureUsernameAvailable,
    ensureEmailAvailable,
    ensureSsnAvailable,
    checking,
  } = useUsernameEmailAvailability(
    isEditMode ? {
      excludePatientId: editId,
      originalUsername: initialPatient?.username || '',
      originalEmail: initialPatient?.email || '',
      originalSsn: initialPatient?.ssn || '',
    } : null
  );

  const systemCaregiverPickerOptions = useMemo(
    () => buildCaregiverOptionsWithStubs(activeSystemCaregivers, formData.systemCaregiverIds),
    [activeSystemCaregivers, formData.systemCaregiverIds]
  );
  const practiceCaregiverPickerOptions = useMemo(
    () => buildCaregiverOptionsWithStubs(activePracticeCaregivers, formData.practiceCaregiverIds),
    [activePracticeCaregivers, formData.practiceCaregiverIds]
  );

  const addSystemCaregiverId = (idStr) => {
    setFormData((prev) => (
      prev.systemCaregiverIds.includes(idStr)
        ? prev
        : { ...prev, systemCaregiverIds: [...prev.systemCaregiverIds, idStr] }
    ));
    clearFieldError(setErrors, 'systemCaregiver');
  };
  const removeSystemCaregiverId = (idStr) => {
    setFormData((prev) => ({ ...prev, systemCaregiverIds: prev.systemCaregiverIds.filter((id) => id !== idStr) }));
  };
  const addPracticeCaregiverId = (idStr) => {
    setFormData((prev) => (
      prev.practiceCaregiverIds.includes(idStr)
        ? prev
        : { ...prev, practiceCaregiverIds: [...prev.practiceCaregiverIds, idStr] }
    ));
    clearFieldError(setErrors, 'practiceCaregiver');
  };
  const removePracticeCaregiverId = (idStr) => {
    setFormData((prev) => ({ ...prev, practiceCaregiverIds: prev.practiceCaregiverIds.filter((id) => id !== idStr) }));
  };

  const pickProviderField = (name, idStr) => {
    setFormData((prev) => ({ ...prev, [name]: idStr }));
    if (name === 'provider') clearFieldError(setErrors, 'provider');
  };

  const availableCarePrograms = useMemo(() => {
    const enrolled = currentPractice?.enrolled_programs;
    if (Array.isArray(enrolled) && enrolled.length > 0) {
      return buildProgramTabsFromEnrolled(enrolled);
    }
    return buildProgramTabsFromEnrolled(getProgramsForPractice(carePrograms, enrolled));
  }, [carePrograms, currentPractice?.enrolled_programs]);

  useEffect(() => {
    if (practiceId) {
      dispatch(fetchSystemCaregivers(practiceId));
      dispatch(fetchPracticeProviders({ practiceId, force: true }));
      dispatch(fetchPracticeCaregivers(practiceId));
      dispatch(fetchPractice(practiceId));
    }
    inventoryService.getProducts({ page: 1, limit: 999 }).then(res => { if (res.success) setProducts(res.data); });

    careProgramService.getCarePrograms()
      .then(res => {
        if (res?.success && Array.isArray(res.data)) {
          setCarePrograms(filterActiveCarePrograms(res.data));
        }
      })
      .catch(() => setCarePrograms([]));

    lookupService.getIcdCodes()
      .then((res) => {
        if (res?.success && Array.isArray(res.data)) {
          setIcdCodesByMeasureType(groupIcdCodesByMeasureType(res.data));
        }
      })
      .catch(() => {
        setIcdCodesByMeasureType({ 1: [], 2: [], 3: [] });
      });
  }, [dispatch, practiceId]);

  useEffect(() => {
    if (availableCarePrograms.length === 0) return;
    // Keep selections empty by default; only drop invalid keys if programs change
    setSelectedCarePrograms((prev) =>
      prev.filter((key) => availableCarePrograms.some((prog) => prog.id === key))
    );
  }, [availableCarePrograms]);

  useEffect(() => {
    updateBreadcrumbs([
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Practice Management', path: `/practices/${practiceId}` },
      { label: 'Patient Management', path: `/practices/${practiceId}?tab=patient` },
      { label: isEditMode ? 'Edit Patient' : 'Add Patient', path: '#' }
    ]);
    return () => clearBreadcrumbs();
  }, [practiceId, isEditMode, updateBreadcrumbs, clearBreadcrumbs]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'status' && isEditMode) {
      const initialStatusId = normalizePatientStatusId(initialPatient?.status);
      const targetStatusId = normalizePatientStatusId(value);
      const hasAssignedDevices = mappedDevices.length > 0;

      if (initialStatusId === 2 && (targetStatusId === 3 || targetStatusId === 4) && hasAssignedDevices) {
        setStatusDeviceRemovalModal({
          show: true,
          targetStatus: value
        });
        return;
      }
    }

    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      setFormData(prev => ({ ...prev, email: cleanedValue }));
      const emailErr = getEmailValidationError(cleanedValue);
      setErrors(prev => {
        const next = { ...prev };
        if (emailErr) next.email = emailErr;
        else delete next.email;
        return next;
      });
      if (!emailErr && cleanedValue) {
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      return;
    }
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        setErrors(prev => {
          const next = { ...prev };
          if (numericValue.length > 0 && numericValue.length < 5) {
            next.zipCode = 'Zip code must be exactly 5 digits';
          } else {
            delete next.zipCode;
          }
          return next;
        });
      }
      return;
    }
    if (name === 'ssn') {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      let formatted = digits;
      if (digits.length > 5) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      setFormData((prev) => ({ ...prev, ssn: formatted }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.ssn;
        return next;
      });
      scheduleSsnCheck(formatted, setErrors);
      return;
    }
    if (name === 'state') {
      setFormData((prev) => ({
        ...prev,
        state: value,
        stateId: getStateIdFromName(value),
      }));
      clearFieldError(setErrors, 'state');
      return;
    }
    if (['homePhone', 'cellPhone', 'fax', 'emergencyContactPhone'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      setErrors((prev) => {
        const next = { ...prev };
        const typingError = getPhoneTypingError(digits, {
          required: name === 'cellPhone',
          requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : name === 'emergencyContactPhone' ? 'Emergency Contact Phone' : 'Fax',
        });
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    clearFieldError(setErrors, name);
  };

  const handleCareProgramChange = (programId) => {
    setSelectedCarePrograms(prev => prev.includes(programId) ? prev.filter(p => p !== programId) : [...prev, programId]);
  };

  const handleMonitoringToggle = (type) => {
    const kind = monitoringVitalKind(type);
    if (kind && !selectedMonitoring.includes(type)) {
      setVitalModal(kind);
      return;
    }
    setSelectedMonitoring((prev) => {
      const isSelected = prev.includes(type);
      if (isSelected) {
        setMonitoringIcdValues((icdPrev) => ({ ...icdPrev, [type]: '' }));
        if (kind) {
          setVitalModes((modes) => ({ ...modes, [kind]: null }));
          setVitalDrafts((drafts) => ({ ...drafts, [kind]: null }));
          vitalConfirmedRef.current[kind] = false;
        }
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  };

  const handleVitalSettingsCancel = () => {
    setVitalModal(null);
  };

  const handleVitalSettingsConfirm = ({ mode, payload }) => {
    const kind = vitalModal;
    if (!kind) return;
    vitalConfirmedRef.current[kind] = true;
    setVitalModes((modes) => ({ ...modes, [kind]: mode }));
    setVitalDrafts((drafts) => ({ ...drafts, [kind]: mode === 'custom' ? payload : null }));
    const type = kind === 'bg' ? 'Blood Glucose' : kind === 'weight' ? 'Weight' : 'Blood Pressure';
    setSelectedMonitoring((prev) => (prev.includes(type) ? prev : [...prev, type]));
    setVitalModal(null);
  };

  const handleMonitoringIcdChange = (type, value) => {
    setMonitoringIcdValues((prev) => ({ ...prev, [type]: value }));
  };

  const handleCancel = () => navigate(-1);

  const handleSubmit = async (e) => {
    const zipStr = String(formData.zipCode || '').trim();
    if (!zipStr) {
      setErrors(prev => ({ ...prev, zipCode: 'Zip Code is required' }));
      toast.error('Zip Code is required');
      return;
    } else if (zipStr.length !== 5) {
      setErrors(prev => ({ ...prev, zipCode: 'Zip code must be exactly 5 digits' }));
      toast.error('Zip code must be exactly 5 digits');
      return;
    }

    if (e) e.preventDefault();
    if (pictureFile) {
      const pictureError = validateImageUploadFile(pictureFile, { label: 'Profile picture' });
      if (pictureError) {
        setErrors((prev) => ({ ...prev, profilePicture: pictureError }));
        toast.error(pictureError);
        return;
      }
    }
    if (!formData.cellPhone?.trim()) {
      setErrors(prev => ({ ...prev, cellPhone: 'Cell Phone is required' }));
      toast.error('Cell Phone is required');
      return;
    }
    const emergencyPhoneError = getPhoneTypingError(extractPhoneDigits(formData.emergencyContactPhone || ''), {
      required: false,
      requiredLabel: 'Emergency Contact Phone'
    });
    if (emergencyPhoneError) {
      setErrors(prev => ({ ...prev, emergencyContactPhone: emergencyPhoneError }));
      toast.error(emergencyPhoneError);
      return;
    }
    const emailErr = getEmailValidationError(formData.email);
    if (emailErr) {
      setErrors(prev => ({ ...prev, email: emailErr }));
      toast.error(emailErr);
      return;
    }
    if (Object.values(errors).some((msg) => AVAILABILITY_ERROR_MESSAGES.has(msg))) {
      toast.warn('Please fix username, email, or SSN conflicts before saving.');
      return;
    }
    const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
    const emailOk = await ensureEmailAvailable(formData.email, setErrors);
    const ssnOk = await ensureSsnAvailable(formData.ssn, setErrors);
    if (!usernameOk || !emailOk || !ssnOk) {
      toast.warn('Please fix username, email, or SSN conflicts before saving.');
      return;
    }
    if (requiresSystemCaregiver && (!formData.systemCaregiverIds || formData.systemCaregiverIds.length === 0)) {
      setErrors((prev) => ({ ...prev, systemCaregiver: 'Select at least one system caregiver' }));
      toast.error('Select at least one system caregiver');
      return;
    }
    if (!formData.practiceCaregiverIds || formData.practiceCaregiverIds.length === 0) {
      setErrors((prev) => ({ ...prev, practiceCaregiver: 'Select at least one practice caregiver' }));
      toast.error('Select at least one practice caregiver');
      return;
    }
    const enrollsInRpm = selectedCarePrograms.includes('rpm');
    const persistVitalChoices = async (patientRowId) => {
      if (!enrollsInRpm || !patientRowId) return;
      const selectedKinds = [
        ['Blood Pressure', 'bp'],
        ['Blood Glucose', 'bg'],
        ['Weight', 'weight']
      ].filter(([type]) => selectedMonitoring.includes(type));
      if (!selectedKinds.some(([, kind]) => vitalConfirmedRef.current[kind])) return;
      const anyCustom = selectedKinds.some(([, kind]) => vitalModes[kind] === 'custom');
      const changedPayload = {};
      selectedKinds.forEach(([, kind]) => {
        if (vitalModes[kind] === 'custom' && vitalDrafts[kind]) {
          Object.assign(changedPayload, vitalDrafts[kind]);
        }
        if (vitalModes[kind] === 'practice' || vitalModes[kind] === 'custom') {
          writeVitalScheduleTargetMode(practiceId, patientRowId, kind, vitalModes[kind]);
        }
      });
      await applyEnrollmentScheduleChoice({
        practiceId,
        patientId: patientRowId,
        usePracticeSettings: !anyCustom,
        changedPayload: anyCustom && Object.keys(changedPayload).length ? changedPayload : null
      });
    };
    setIsSubmitting(true);
    try {
      const systemCaregiverIdsNum = requiresSystemCaregiver
        ? toNumericCaregiverIds(formData.systemCaregiverIds)
        : [];
      const practiceCaregiverIdsNum = toNumericCaregiverIds(formData.practiceCaregiverIds);
      const patientPayload = {
        ...formData,
        phone: (formData.cellPhone || '').replace(/\D/g, ''),
        cell_phone: (formData.homePhone || '').replace(/\D/g, ''),
        homePhone: (formData.cellPhone || '').replace(/\D/g, ''),
        cellPhone: (formData.homePhone || '').replace(/\D/g, ''),
        mobile_app_access: formData.mobileAppAccess === 'Enabled' ? 1 : 0,
        practice_id: parseInt(practiceId),
        care_programs: selectedCarePrograms,
        monitoring_types: buildMonitoringTypesPayload(selectedMonitoring, monitoringIcdValues),
        chronic_conditions: serializePatientChronicConditions(selectedChronicConditions),
        ccm_restrictions_confirmed: isEditMode ? true : ccmRestrictionsConfirmed,
        practice_caregiver_id: practiceCaregiverIdsNum.length > 0 ? String(practiceCaregiverIdsNum[0]) : '',
        practice_caregiver_ids: practiceCaregiverIdsNum,
        practiceCaregiverIds: practiceCaregiverIdsNum,
        system_caregiver_id: systemCaregiverIdsNum.length > 0 ? String(systemCaregiverIdsNum[0]) : '',
        system_caregiver_ids: systemCaregiverIdsNum,
        systemCaregiverIds: systemCaregiverIdsNum,
      };
      // Build FormData so we can include the profile picture file
      const patientFD = new FormData();
      if (pictureFile) {
        patientFD.append('profilePicture', pictureFile);
      }
      Object.entries(patientPayload).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          patientFD.append(key, '');
        } else if (typeof value === 'object' && !(value instanceof File)) {
          patientFD.append(key, JSON.stringify(value));
        } else {
          patientFD.append(key, String(value));
        }
      });
      if (isEditMode) await dispatch(updatePatient({ practiceId, patientId: editId, patientData: patientFD })).unwrap();
      else {
        const createResult = await dispatch(createPatient({ practiceId, patientData: patientFD })).unwrap();
        const newPatientRowId =
          createResult?.data?.patient_table_id ??
          createResult?.patient_table_id ??
          createResult?.data?.id ??
          null;
        if (newPatientRowId) {
          try {
            await persistVitalChoices(newPatientRowId);
          } catch (scheduleErr) {
            toast.warning(
              scheduleErr?.message ||
                'Patient was saved, but the custom schedule and target settings could not be saved. You can update them on the patient page.'
            );
          }
        }
      }
      if (isEditMode) {
        try {
          await persistVitalChoices(editId);
        } catch (scheduleErr) {
          toast.warning(
            scheduleErr?.message ||
              'Patient was saved, but the custom schedule and target settings could not be saved. You can update them on the patient page.'
          );
        }
      }
      toast.success('Patient saved successfully');
      handleCancel();
    } catch (error) {
      if (applyPasswordServerFieldError(setErrors, error)) {
        return;
      }
      const apiMsg = error?.response?.data?.message || error?.message || '';
      const lower = apiMsg.toLowerCase();
      if (lower.includes('username') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate') || lower.includes('in use') || lower.includes('already'))) {
        setErrors(prev => ({ ...prev, username: 'This username is already taken. Please choose a different username.' }));
      }
      if (lower.includes('email') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate') || lower.includes('in use') || lower.includes('already'))) {
        setErrors(prev => ({ ...prev, email: 'This email address is already registered. Please use a different email.' }));
      }
      toast.error(apiMsg || 'Failed to save patient');
    }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="add-patient-wrapper full-page">
      <div className="add-patient-card full-width">
        <div className="ap-header">
          <h4>Patient Intake Form</h4>
        </div>

        <div className="ap-practice-info-strip">
          <h5 className="ap-practice-title">{currentPractice?.practice_name || 'Healthy Heart Cardiology'}</h5>
          <div className="ap-header-logos">
            {availableCarePrograms.map(prog => (
              <span key={prog.id} className="ap-header-logo-item" style={{ color: prog.color }}>
                <FontAwesomeIcon icon={prog.icon} /> {prog.label}
              </span>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="ap-main-grid-parity ap-form-standard-layout">
            <div className="pif-demo-section-label" style={{ gridColumn: '1 / -1' }}>Personal Information</div>

            <div className="ap-row-split">
              <div className="ap-form-group col-small"><label>Title</label><select name="title" value={formData.title} onChange={handleInputChange}><option value="">-</option><option value="Dr.">Dr.</option></select></div>
              <div className="ap-form-group col-large"><label>First Name <span className="required">*</span></label><input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" /></div>
            </div>
            <div className="ap-row-split">
              <div className="ap-form-group col-small"><label>Middle Name</label><input type="text" name="middleInitial" value={formData.middleInitial || ''} onChange={handleInputChange} placeholder="e.g., Marie" maxLength={20} /></div>
              <div className="ap-form-group col-large"><label>Last Name <span className="required">*</span></label><input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" /></div>
            </div>
            <div className="ap-form-group"><label>Gender <span className="required">*</span></label><select name="gender" value={formData.gender || 'Male'} onChange={handleInputChange}><option value="Male">Male</option><option value="Female">Female</option></select></div>
            <div className="ap-row-split">
              <div className="ap-form-group"><label>Date of Birth <span className="required">*</span></label><AppDateInput name="dateOfBirth" value={formData.dateOfBirth || ''} onChange={(v) => handleInputChange({ target: { name: 'dateOfBirth', value: v } })} /></div>
              <div className="ap-form-group">
                <label>SSN</label>
                <input
                  type="text"
                  name="ssn"
                  value={formData.ssn || ''}
                  placeholder="000-00-0000"
                  onChange={handleInputChange}
                  onBlur={() => {
                    const digits = String(formData.ssn || '').replace(/\D/g, '');
                    if (digits.length === 9) scheduleSsnCheck(formData.ssn, setErrors);
                  }}
                />
                {errors.ssn && <span className="error-text">{errors.ssn}</span>}
              </div>
            </div>
            <div className="ap-row-split">
              <div className="ap-form-group"><label>Home Phone</label><PhoneNumberInput name="homePhone" value={formData.homePhone || ''} placeholder="(000) 000-0000" onChange={handleInputChange} /></div>
              <div className="ap-form-group"><label>Cell Phone <span className="required">*</span></label><PhoneNumberInput name="cellPhone" value={formData.cellPhone || ''} placeholder="(000) 000-0000" onChange={handleInputChange} required /></div>
            </div>
            <div className="ap-form-group"><label>Fax</label><PhoneNumberInput name="fax" value={formData.fax || ''} placeholder="(000) 000-0000" onChange={handleInputChange} /></div>

            <div className="pif-demo-section-label" style={{ gridColumn: '1 / -1', marginTop: '8px' }}>Emergency Contact</div>
            <div className="ap-row-split">
              <div className="ap-form-group"><label>First Name</label><input type="text" name="emergencyContactFirstName" value={formData.emergencyContactFirstName || ''} onChange={handleInputChange} placeholder="e.g., Jane" maxLength={80} /></div>
              <div className="ap-form-group"><label>Last Name</label><input type="text" name="emergencyContactLastName" value={formData.emergencyContactLastName || ''} onChange={handleInputChange} placeholder="e.g., Doe" maxLength={80} /></div>
            </div>
            <div className="ap-row-split">
              <div className="ap-form-group"><label>Relationship to Patient</label><select name="emergencyContactRelation" value={formData.emergencyContactRelation || ''} onChange={handleInputChange}><option value="">Select Relationship</option>{EMERGENCY_CONTACT_RELATIONS.map((relation) => <option key={relation} value={relation}>{relation}</option>)}</select></div>
              <div className="ap-form-group">
                <label>Phone</label>
                <PhoneNumberInput name="emergencyContactPhone" value={formData.emergencyContactPhone || ''} placeholder="(000) 000-0000" onChange={handleInputChange} />
                {errors.emergencyContactPhone && <span className="error-text">{errors.emergencyContactPhone}</span>}
              </div>
            </div>
            <div className="ap-form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Address</label>
              <input type="text" name="emergencyContactAddress" value={formData.emergencyContactAddress || ''} onChange={handleInputChange} placeholder="e.g., 123 Main St, New York, NY 10001" maxLength={250} />
            </div>

            <div className="pif-demo-section-label" style={{ gridColumn: '1 / -1', marginTop: '8px' }}>Account Credentials</div>

            <div className="ap-form-group">
              <label>
                Username <span className="required">*</span>
                {checking?.username && <span style={{ fontSize: '11px', color: '#6366f1', marginLeft: '6px' }}>(checking...)</span>}
              </label>
              <input type="text" name="username" value={formData.username || ''} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} placeholder="e.g., john.doe" maxLength={USERNAME_MAX_LENGTH} />
              {errors.username && <span className="error-text">{errors.username}</span>}
            </div>
            <div className="ap-form-group">
              <label>
                Email <span className="required">*</span>
                {checking?.email && <span style={{ fontSize: '11px', color: '#6366f1', marginLeft: '6px' }}>(checking...)</span>}
              </label>
              <input type="email" name="email" value={formData.email || ''} placeholder="e.g., patient@example.com" onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} onBlur={() => handleEmailBlur(formData.email, setErrors)} />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
            <div className="ap-form-group"><label>Password <span className="required">*</span></label><input type="password" name="password" className={errors.password ? 'is-invalid' : ''} value={formData.password || ''} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} /><small className="ap-help-text-parity">Password must be 8 characters with uppercase, lowercase, digit, with no special character.</small>{errors.password && <span className="error-text">{errors.password}</span>}</div>
            <div className="ap-form-group"><label>Confirm Password <span className="required">*</span></label><input type="password" name="confirmPassword" value={formData.confirmPassword || ''} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} /></div>

            <div className="pif-demo-section-label" style={{ gridColumn: '1 / -1', marginTop: '8px' }}>Address & Location</div>

            <div className="ap-form-group"><label>Address Line 1 <span className="required">*</span></label><input type="text" name="addressLine1" value={formData.addressLine1 || ''} onChange={handleInputChange} placeholder="e.g., 123 Main St" /></div>
            <div className="ap-form-group"><label>Address Line 2</label><input type="text" name="addressLine2" value={formData.addressLine2 || ''} onChange={handleInputChange} placeholder="e.g., Apt 4B" /></div>
            <div className="ap-form-group"><label>City <span className="required">*</span></label><input type="text" name="city" value={formData.city || ''} onChange={handleInputChange} placeholder="e.g., New York" /></div>
            <div className="ap-row-split">
              <div className="ap-form-group"><label>State <span className="required">*</span></label><select name="state" value={formData.state || ''} onChange={handleInputChange}><option value="">Select State</option>{US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
              <div className="ap-form-group"><label>Zip Code <span className="required">*</span></label><input type="text" name="zipCode" value={formData.zipCode || ''} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" />{errors.zipCode && <span className="error-text">{errors.zipCode}</span>}</div>
            </div>
            <div className="ap-form-group"><label>Time Zone <span className="required">*</span></label><select name="timeZone" value={formData.timeZone || ''} onChange={handleInputChange}><option value="">Select Time Zone</option>{TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}</select></div>

            <div className="pif-demo-section-label" style={{ gridColumn: '1 / -1', marginTop: '8px' }}>Care Team & Settings</div>

            <div className="ap-form-group"><label>Status <span className="required">*</span></label><select name="status" value={formData.status || 'Active'} onChange={handleInputChange}><option value="Active">Active</option><option value="Pending">Pending</option><option value="Locked">Locked</option></select></div>
            <div className="ap-form-group"><label>Two Way Authentication <span className="required">*</span></label><select name="twoWayAuth" value={formData.twoWayAuth || 'Enabled'} onChange={handleInputChange}><option value="Enabled">Enabled</option><option value="Disabled">Disabled</option></select></div>
            <div className="ap-form-group"><label>Mobile App Access</label><select name="mobileAppAccess" value={formData.mobileAppAccess || 'Disabled'} onChange={handleInputChange}><option value="Disabled">Off</option><option value="Enabled">On</option></select></div>
            <div className="ap-form-group"><label>RPM Start Date <span className="required">*</span></label><AppDateInput name="rpmStartDate" value={formData.rpmStartDate || ''} onChange={(v) => handleInputChange({ target: { name: 'rpmStartDate', value: v } })} /></div>
            <PatientAscStyleSinglePicker
              label="Provider"
              name="provider"
              required
              value={formData.provider || ''}
              options={activePracticeProviders}
              error={errors.provider}
              placeholder={activePracticeProviders.length === 0 ? 'No providers available' : 'Select provider'}
              emptyMessage="No providers available"
              onPick={pickProviderField}
            />
            {requiresSystemCaregiver && (
            <PatientAscStyleMultiPicker
              label="System Caregiver"
              required
              placeholder="Select system caregivers"
              emptyMessage="No system caregivers available"
              selectedIds={formData.systemCaregiverIds}
              options={systemCaregiverPickerOptions}
              onAddId={addSystemCaregiverId}
              onRemoveId={removeSystemCaregiverId}
              error={errors.systemCaregiver}
            />
            )}
            <PatientAscStyleMultiPicker
              label="Practice Caregiver"
              placeholder="Select practice caregivers"
              emptyMessage="No practice caregivers available"
              selectedIds={formData.practiceCaregiverIds}
              options={practiceCaregiverPickerOptions}
              onAddId={addPracticeCaregiverId}
              onRemoveId={removePracticeCaregiverId}
              required
              error={errors.practiceCaregiver}
            />

            <div style={{ gridColumn: '1 / -1' }}>
              <div className="ap-form-group">
                <label>Upload Picture</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {/* Filename display — click opens picker */}
                  <div
                    style={{
                      flex: 1, minWidth: 0, padding: '9px 14px',
                      border: `1px solid ${errors.profilePicture ? '#dc3545' : '#e2e8f0'}`, borderRadius: '8px',
                      background: '#fff', cursor: 'pointer',
                      fontSize: '13px', color: pictureFile ? '#1e293b' : '#94a3b8',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}
                    onClick={() => pictureInputRef.current && pictureInputRef.current.click()}
                  >
                    {pictureFile ? pictureFile.name : 'No file chosen (max 1 MB)'}
                  </div>
                  {/* Upload button */}
                  <button
                    type="button"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '9px 18px', background: 'var(--ev-btn-primary)', color: '#fff',
                      border: 'none', borderRadius: '8px', fontWeight: 600,
                      fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
                    }}
                    onClick={() => pictureInputRef.current && pictureInputRef.current.click()}
                  >
                    <FontAwesomeIcon icon={faCloudUploadAlt} /> Upload File
                  </button>
                  {/* Hidden file input */}
                  <input
                    ref={pictureInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={handlePictureChange}
                  />
                  {/* Inline preview */}
                  {pictureFile && (
                    <>
                      <img
                        src={URL.createObjectURL(pictureFile)}
                        alt="Profile Preview"
                        style={{
                          height: '40px', width: '40px', objectFit: 'cover',
                          borderRadius: '50%', border: '2px solid #e0e0e0', flexShrink: 0
                        }}
                      />
                      <button
                        type="button"
                        style={{
                          background: 'none', border: 'none', color: '#d32f2f',
                          cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                          flexShrink: 0, padding: '0 4px'
                        }}
                        onClick={handleRemovePicture}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
                {errors.profilePicture && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                    {errors.profilePicture}
                  </div>
                )}
              </div>
            </div>
          </div>


          <div className="ap-section-parity">
            <h5 className="ap-section-title-parity">Enroll in Care Programs <span className="required">*</span></h5>
            <div className="ap-programs-row-parity">
              {availableCarePrograms.length === 0 ? (
                <p className="text-muted">No care programs are enrolled for this practice.</p>
              ) : availableCarePrograms.map(prog => (
                <div key={prog.id} className={`ap-prog-card-parity ${selectedCarePrograms.includes(prog.id) ? 'selected' : ''}`} onClick={() => handleCareProgramChange(prog.id)}>
                  <div className="ap-prog-check-square">
                    {selectedCarePrograms.includes(prog.id) && <FontAwesomeIcon icon={faCheck} className="ap-check-icon" />}
                  </div>
                  <span className="ap-prog-label-parity" style={{ color: prog.color }}><FontAwesomeIcon icon={prog.icon} /> {prog.label}</span>
                </div>
              ))}
            </div>
          </div>

          {selectedCarePrograms.includes('ccm') && (
            <div className="ap-section-ccm-parity-final mt-10">
              <h5 className="ap-section-title-parity">Qualifying Chronic Health Conditions <span className="required">*</span></h5>
              <p className="ap-note-parity">Select at least two chronic conditions. Patient must have 2+ chronic conditions expected to last at least 12 months.</p>

              <PatientChronicConditionsSection
                conditions={selectedChronicConditions}
                onChange={setSelectedChronicConditions}
                minRequired={2}
                title="Qualifying Chronic Health Conditions"
                description="Select at least two chronic conditions. Patient must have 2+ chronic conditions expected to last at least 12 months."
              />

              {!isEditMode && (
                <>
                  <div className="ap-billing-box-parity">
                    <h6>CCM Billing Restrictions</h6>
                    <p>CCM cannot be billed in the same month as:</p>
                    <ul>
                      <li>Transitional Care Management (TCM) - CPT 99495/99496</li>
                      <li>End-Stage Renal Disease (ESRD) services</li>
                      <li>Program of All-Inclusive Care for the Elderly (PACE)</li>
                      <li>Hospice care</li>
                    </ul>
                  </div>

                  <div className="ap-confirm-row-parity-final" onClick={() => setCcmRestrictionsConfirmed(!ccmRestrictionsConfirmed)}>
                    <div className="ap-prog-check-square-dark">
                      {ccmRestrictionsConfirmed && <FontAwesomeIcon icon={faCheck} className="ap-check-icon-white" />}
                    </div>
                    <span>I confirm that this patient has no active enrollment in Transitional Care Management (TCM), ESRD services, PACE, or Hospice care for the current billing month.</span>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="ap-section-parity">
            <h5 className="ap-section-title-parity">Meter Mapping</h5>
            <div className="ap-meter-row-parity-final">
              <div className="ap-form-group"><label>Model Number</label><select><option value="">Select Model(s)</option>{products.map(p => <option key={p.id} value={p.id}>{formatMeterProductOptionLabel(p)}</option>)}</select></div>
              <div className="ap-form-group"><label>Extension</label><input type="text" /></div>
              <div className="ap-form-group"><label>Serial Number</label><input type="text" /></div>
              <div className="ap-form-group"><label>Gateway Serial Number</label><input type="text" /></div>
            </div>
          </div>

          <div className="ap-section-parity">
            <h5 className="ap-section-title-parity">Type of Monitoring <span className="required">*</span></h5>
            <div className="ap-monitoring-panel">
              {['Blood Pressure', 'Blood Glucose', 'Weight'].map((type) => {
                const isSelected = selectedMonitoring.includes(type);
                const options = getIcdOptionsForMonitoringLabel(type, icdCodesByMeasureType);
                const currentIcd = monitoringIcdValues[type] || '';
                const inputId = `ap-monitoring-${type.replace(/\s+/g, '-').toLowerCase()}`;
                return (
                  <div key={type} className={`ap-monitoring-row-item ${isSelected ? 'is-selected' : ''}`}>
                    <label htmlFor={inputId} className="ap-monitoring-label">
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleMonitoringToggle(type)}
                        className="ap-monitoring-checkbox"
                      />
                      <span>
                        {type}
                        {(() => {
                          const kind = monitoringVitalKind(type);
                          const mode = kind ? vitalModes[kind] : null;
                          if (!kind || !isSelected || !mode) return null;
                          return (
                          <button
                            type="button"
                            className="pif-monitoring-bp-note"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setVitalModal(kind);
                            }}
                          >
                            {mode === 'custom' ? 'Using custom settings' : 'Using practice settings'}
                          </button>
                          );
                        })()}
                      </span>
                    </label>
                    <div className="ap-monitoring-icd-field">
                      <span className="ap-monitoring-icd-label">ICD Code</span>
                      <IcdCodeSearchField
                        value={currentIcd}
                        localOptions={options}
                        disabled={!isSelected}
                        onChange={(code) => handleMonitoringIcdChange(type, code)}
                        ariaLabel={`ICD code for ${type}`}
                        inputClassName="ap-monitoring-icd-select"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ap-section-parity">
            <h5 className="ap-section-title-parity">Insurance</h5>
            <div className="ap-insurance-grid-parity-final">
              <div className="ap-form-group"><label>Primary Insurance Name</label><input type="text" /></div>
              <div className="ap-form-group"><label>Subscriber Id</label><input type="text" /></div>
              <div className="ap-form-group"><label>Insured's Name</label><input type="text" /></div>
              <div className="ap-form-group"><label>Group No.</label><input type="text" /></div>

              <div className="ap-form-group"><label>Secondary Insurance Name</label><input type="text" /></div>
              <div className="ap-form-group"><label>Subscriber Id</label><input type="text" /></div>
              <div className="ap-form-group"><label>Insured's Name</label><input type="text" /></div>
              <div className="ap-form-group"><label>Group No.</label><input type="text" /></div>
            </div>
          </div>

          <div className="ap-section-parity">
            <h5 className="ap-section-title-parity">Upload Documents</h5>
            <div className="ap-docs-row-parity-final">
              <div className="ap-form-group"><label>File Name</label><input type="text" /></div>
              <div className="ap-form-group"><label>Upload File</label>
                <div className="ap-upload-parity-box-final">Choose File No file chosen</div>
              </div>
            </div>
          </div>

          <div className="ap-actions-parity">
            <button type="button" className="btn-cancel-parity" onClick={handleCancel}>Cancel</button>
            <button type="submit" className="btn-save-parity" disabled={isSubmitting}>
              <FontAwesomeIcon icon={faSave} /> {isSubmitting ? 'Saving...' : 'Save Patient'}
            </button>
          </div>
        </form>
      </div>

      <PatientStatusDeviceRemovalModal
        show={statusDeviceRemovalModal.show}
        patientName={[formData.firstName, formData.lastName].filter(Boolean).join(' ') || 'this patient'}
        targetStatus={statusDeviceRemovalModal.targetStatus}
        devices={mappedDevices}
        onConfirm={handleConfirmStatusDeviceRemoval}
        onCancel={handleCancelStatusDeviceRemoval}
      />
      <BpEnrollmentSettingsModal
        open={vitalModal != null}
        kind={vitalModal || 'bp'}
        practiceId={practiceId}
        initialMode={vitalModal ? vitalModes[vitalModal] : null}
        onCancel={handleVitalSettingsCancel}
        onConfirm={handleVitalSettingsConfirm}
      />
    </div>
  );
};

export default AddPatient;